import { formatPhone } from "@src/utils/strings";
import { Avatar, Stack, useTheme } from "@mui/material";
import { Tooltip } from "@mui/material";

import { NearbyWorkerSearchResult, WorkerStatusLabel } from "./useGetNearbyWorkers";
import { isDefined } from "@clipboard-health/util-ts";
import { useAgentProfilePicture } from "@src/appV2/ShiftBlock/hooks/useGetAgentProfilePicture";
import { StarProfessionalTag } from "../../../facilityDashboard/ShiftCalendar/dayView/facilityShiftCard/starProfessionalTag";

export type NearbyWorker = NearbyWorkerSearchResult & { isStarred?: boolean };

interface NearbyWorkerResultProps {
  renderProps: React.HTMLAttributes<HTMLLIElement>;
  nearbyWorker: NearbyWorker;
}

export function NearbyWorkerResult(props: NearbyWorkerResultProps) {
  const { renderProps, nearbyWorker } = props;
  const { workerId, fullName, statusLabel, phoneNumber, isStarred } = nearbyWorker;
  const theme = useTheme();

  const DISPLAY_STATUS_MAP = {
    [WorkerStatusLabel.Restricted]: {
      text: "Reports of poor attendance or unprofessional conduct",
      color: theme.palette.warning.main,
    },
    [WorkerStatusLabel.Blocked]: { text: "Blocked Professional", color: theme.palette.error.main },
  };
  // @ts-expect-error TODO(typecheck-unify): TS7053 Element implicitly has an 'any' type because expression of type 'WorkerStatusLabel' can't be used to index type '{ Restricted: { text:
  const activeDisplayStatus = isDefined(statusLabel) ? DISPLAY_STATUS_MAP[statusLabel] : undefined;

  const { data: agentProfileImage } = useAgentProfilePicture({ agentId: workerId });

  const isBlockedWorker = statusLabel === WorkerStatusLabel.Blocked;

  return (
    <Tooltip
      title="This professional has been blocked by your workplace and cannot be invited"
      disableHoverListener={!isBlockedWorker}
      componentsProps={{
        tooltip: {
          sx: {
            margin: "2px !important",
          },
        },
      }}
    >
      {/* FIXME: Automatically disabled during ESLint config update */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <li
        {...renderProps}
        onClick={(event) => {
          if (isBlockedWorker) {
            event.preventDefault();
            event.stopPropagation();
          } else {
            renderProps.onClick?.(event);
          }
        }}
      >
        <Stack spacing={2} alignItems="center" direction="row">
          <Avatar src={agentProfileImage?.distribution_url}></Avatar>
          <Stack direction="column" spacing={0.25}>
            <Stack>
              <span>{phoneNumber ? `${fullName}, ${formatPhone(phoneNumber)}` : fullName}</span>
              {isDefined(activeDisplayStatus) && (
                <Stack sx={{ variant: "body2", color: activeDisplayStatus.color, mt: -0.5 }}>
                  {activeDisplayStatus.text}
                </Stack>
              )}
            </Stack>
            {isStarred && <StarProfessionalTag />}
          </Stack>
        </Stack>
      </li>
    </Tooltip>
  );
}
